import React from 'react';
import { ChipProps } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ExclamationPointIcon from '@mui/icons-material/PriorityHighOutlined';
import { Color } from '../../theme/colors';
import FilledChip from './FilledChip';

export type ChipStatus =
  | 'ACTIVE'
  | 'PENDING'
  | 'EXPIRED'
  | 'UNVERIFIED'
  | 'INELIGIBLE'
  | 'LEGACY_DELETED'
  | 'LEGACY_DEACTIVATED'
  | 'LEGACY_65_PLUS';

export interface StatusChipProps extends Omit<ChipProps, 'color'> {
  status: ChipStatus;
  isDarkMode?: boolean;
}

export function StatusChip({ isDarkMode = true, status, sx, ...props }: StatusChipProps): React.JSX.Element {
  let label = '';
  let backgroundColor = '';
  let iconColor = '';
  let icon: React.ReactElement | undefined;

  switch (status) {
    case 'ACTIVE':
      label = 'Active';
      backgroundColor = isDarkMode ? Color.SeaGreen100 : Color.SeaGreen600;
      iconColor = isDarkMode ? Color.SeaGreen600 : Color.SeaGreen100;
      icon = <GppGoodOutlinedIcon />;
      break;
    case 'EXPIRED':
      label = 'Expired';
      backgroundColor = isDarkMode ? Color.Apricot50 : Color.Apricot600;
      iconColor = isDarkMode ? Color.Apricot600 : Color.Apricot50;
      icon = <GppMaybeOutlinedIcon />;
      break;
    case 'PENDING':
      label = 'Pending';
      backgroundColor = isDarkMode ? Color.Slate100 : Color.Slate500;
      iconColor = isDarkMode ? Color.Slate500 : Color.Slate100;
      icon = <GppMaybeOutlinedIcon />;
      break;
    case 'UNVERIFIED':
      label = 'Unverified';
      backgroundColor = isDarkMode ? Color.Slate100 : Color.Slate500;
      iconColor = isDarkMode ? Color.Slate500 : Color.Slate100;
      icon = <HelpOutlineOutlinedIcon />;
      break;
    case 'INELIGIBLE':
      label = 'Ineligible';
      backgroundColor = isDarkMode ? Color.Raspberry100 : Color.Raspberry600;
      iconColor = isDarkMode ? Color.Raspberry600 : Color.Raspberry100;
      icon = <BlockOutlinedIcon />;
      break;
    case 'LEGACY_DELETED':
      label = 'Deleted';
      backgroundColor = isDarkMode ? Color.Raspberry100 : Color.Raspberry600;
      iconColor = isDarkMode ? Color.Raspberry600 : Color.Raspberry100;
      icon = undefined;
      break;
    case 'LEGACY_DEACTIVATED':
      label = 'Deactivated';
      backgroundColor = isDarkMode ? Color.Raspberry100 : Color.Raspberry600;
      iconColor = isDarkMode ? Color.Raspberry600 : Color.Raspberry100;
      icon = undefined;
      break;
    case 'LEGACY_65_PLUS':
      label = undefined;
      backgroundColor = isDarkMode ? Color.Raspberry100 : Color.Raspberry600;
      iconColor = isDarkMode ? Color.Raspberry600 : Color.Raspberry100;
      icon = <ExclamationPointIcon sx={{ marginLeft: '5px' }} />;
      break;
  }

  return (
    <FilledChip
      label={label}
      icon={icon}
      size='small'
      {...props}
      sx={[
        {
          backgroundColor: backgroundColor,
          color: iconColor,
          fontSize: 12,
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: iconColor,
            fontSize: '14px',
          },
          maxWidth: status === 'LEGACY_65_PLUS' ? '24px' : undefined,
          paddingLeft: status === 'LEGACY_65_PLUS' ? '7px' : undefined,
        },
        ...(Array.isArray(sx) ? sx : [sx ? sx : {}]),
      ]}
    />
  );
}

export default StatusChip;
